<template>
    <div class="ix-dashboard-block bordered">
        <div class="d-flex align-items-end flex-wrap" style="gap: 12px;">
            <div v-if="!articleId" class="form-group mb-0" style="min-width: 260px; flex-grow: 1; max-width: 420px;">
                <label class="mb-1 d-block text-muted" style="font-size: 12px;">Famille d'articles</label>
                <multiselect
                    v-model="selectedFamilies"
                    :options="familyOptions"
                    track-by="id"
                    label="label"
                    :searchable="true"
                    :multiple="true"
                    :close-on-select="false"
                    placeholder="Toutes les familles"
                    selectLabel=""
                    selectedLabel="Sélectionné"
                    deselectLabel=""
                    @input="onFilterChange">
                    <template slot="noOptions">Aucune famille.</template>
                </multiselect>
            </div>

            <div v-if="!articleId" class="form-group mb-0" style="min-width: 240px; flex-grow: 1; max-width: 360px;">
                <label class="mb-1 d-block text-muted" style="font-size: 12px;">Recherche</label>
                <input type="text"
                       class="form-control"
                       placeholder="Référence interne, EAN, désignation..."
                       v-model="search"
                       @input="onSearchInput">
            </div>

            <div class="form-group mb-0" style="min-width: 190px;">
                <label for="trade-stats-preset" class="mb-1 d-block text-muted" style="font-size: 12px;">Période</label>
                <select id="trade-stats-preset" class="form-control" v-model="selectedPreset" @change="onPresetChange">
                    <option v-for="preset in presets" :key="preset.key" :value="preset.key">{{ preset.label }}</option>
                    <option value="custom">Personnalisée</option>
                </select>
            </div>

            <div class="form-group mb-0">
                <label for="trade-stats-start" class="mb-1 d-block text-muted" style="font-size: 12px;">Du</label>
                <input type="date" id="trade-stats-start" class="form-control" style="width: auto;" v-model="startAt" @change="onDateChange">
            </div>
            <div class="form-group mb-0">
                <label for="trade-stats-end" class="mb-1 d-block text-muted" style="font-size: 12px;">Au</label>
                <input type="date" id="trade-stats-end" class="form-control" style="width: auto;" v-model="endAt" @change="onDateChange">
            </div>
        </div>

        <div v-if="!articleId" class="custom-control custom-switch mt-3">
            <input type="checkbox" class="custom-control-input" id="trade-stats-subfamilies" v-model="includeSubfamilies" @change="onFilterChange">
            <label class="custom-control-label" for="trade-stats-subfamilies">Inclure les sous-familles</label>
        </div>

        <div class="row mt-4">
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-page-title-secondary mb-3">Achat</h2>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">Montant cumulé HT</span>
                        <strong>{{ loading ? '-' : formatCurrency(synthesis.achats.total_ht) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">Quantité d'articles</span>
                        <strong>{{ loading ? '-' : synthesis.achats.quantity }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                        <span class="text-muted">Prix d'achat moyen</span>
                        <strong>{{ loading ? '-' : formatCurrency(synthesis.achats.average_price) }}</strong>
                    </div>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-page-title-secondary mb-3">Ventes</h2>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">Montant cumulé HT</span>
                        <strong>{{ loading ? '-' : formatCurrency(synthesis.ventes.total_ht) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">Quantité d'articles</span>
                        <strong>{{ loading ? '-' : synthesis.ventes.quantity }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">Prix de vente moyen</span>
                        <strong>{{ loading ? '-' : formatCurrency(synthesis.ventes.average_price) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                        <span class="text-muted">
                            Évolution du prix moyen vs N-1
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Écart en % du prix de vente moyen par rapport à la même période l'année précédente. Non calculable sur un cumul depuis l'origine."></i>
                        </span>
                        <strong :style="priceEvolutionStyle">{{ loading ? '-' : formatEvolutionPercent(synthesis.ventes.average_price_evolution_percent) }}</strong>
                    </div>
                </div>
            </div>
            <div class="col-sm-12 col-md-4">
                <div class="ix-count-box h-100">
                    <h2 class="ix-page-title-secondary mb-3">Marge</h2>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">
                            Marge HT
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Ventes HT − quantité vendue × PAMP de l'article."></i>
                        </span>
                        <strong>{{ loading ? '-' : formatCurrency(synthesis.marge.total_ht) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted">
                            {{ rateLabel }}
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" :title="rateFormula"></i>
                        </span>
                        <strong>{{ loading ? '-' : formatPercent(synthesis.marge.taux) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between">
                        <span class="text-muted">
                            Évolution vs N-1
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" :title="rateEvolutionTooltip"></i>
                        </span>
                        <strong :style="evolutionStyle">{{ loading ? '-' : formatEvolution(synthesis.marge.evolution_points) }}</strong>
                    </div>
                    <p v-if="!loading && synthesis.marge.uncosted_ca_ht > 0" class="text-muted mt-2 mb-0" style="font-size: 12px;">
                        Hors {{ formatCurrency(synthesis.marge.uncosted_ca_ht) }} de ventes d'articles sans PAMP
                    </p>
                </div>
            </div>
        </div>

        <div v-if="!articleId" class="row mt-4">
            <div class="col-sm-12 col-md-6">
                <div class="ix-count-box h-100">
                    <h2 class="ix-page-title-secondary mb-3">Top 3 CA</h2>
                    <div v-if="loading" class="text-center text-muted"><i class="far fa-circle-notch fa-spin"></i></div>
                    <div v-else-if="top3.ca.length === 0" class="text-muted">Aucun article sur ce périmètre</div>
                    <div v-else v-for="article in top3.ca" :key="'ca-' + article.id" class="d-flex justify-content-between mb-1">
                        <a :href="article.url" class="ix-link">{{ article.reference || article.name }}</a>
                        <strong>{{ formatCurrency(article.ca_total) }}</strong>
                    </div>
                </div>
            </div>
            <div class="col-sm-12 col-md-6">
                <div class="ix-count-box h-100">
                    <h2 class="ix-page-title-secondary mb-3">Top 3 marge</h2>
                    <div v-if="loading" class="text-center text-muted"><i class="far fa-circle-notch fa-spin"></i></div>
                    <div v-else-if="top3.marge.length === 0" class="text-muted">Aucun article sur ce périmètre</div>
                    <div v-else v-for="article in top3.marge" :key="'marge-' + article.id" class="d-flex justify-content-between mb-1">
                        <a :href="article.url" class="ix-link">{{ article.reference || article.name }}</a>
                        <strong>{{ formatCurrency(article.marge_total) }}</strong>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!articleId" class="ix-table-box mt-4">
            <table class="ix-table ix-table-sm ix-table-align-middle">
                <thead>
                    <tr>
                        <th>
                            <button class="ix-table-btn-order" @click="sortBy('reference')">Référence
                                <i v-if="orderBy === 'reference'" class="fas" :class="caretClass"></i>
                            </button>
                        </th>
                        <th>
                            <button class="ix-table-btn-order" @click="sortBy('name')">Désignation
                                <i v-if="orderBy === 'name'" class="fas" :class="caretClass"></i>
                            </button>
                        </th>
                        <th>Famille</th>
                        <th class="text-right">
                            <button class="ix-table-btn-order" @click="sortBy('ha_total')">Achat
                                <i v-if="orderBy === 'ha_total'" class="fas" :class="caretClass"></i>
                            </button>
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Total acheté pour cet article sur la période, remises déduites. Sont comptées les lignes d'achat des dépenses validées, qu'il s'agisse de fournitures, de services ou de locations. Une dépense non validée n'est pas comptée."></i>
                        </th>
                        <th class="text-right">
                            <button class="ix-table-btn-order" @click="sortBy('ca_total')">CA cumulé
                                <i v-if="orderBy === 'ca_total'" class="fas" :class="caretClass"></i>
                            </button>
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="Factures validées moins avoirs validés, remises déduites."></i>
                        </th>
                        <th class="text-right">
                            <button class="ix-table-btn-order" @click="sortBy('marge_total')">Marge
                                <i v-if="orderBy === 'marge_total'" class="fas" :class="caretClass"></i>
                            </button>
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="CA − quantité vendue × PAMP de l'article."></i>
                        </th>
                        <th class="text-right">
                            <button class="ix-table-btn-order" @click="sortBy('marge_taux')">Taux
                                <i v-if="orderBy === 'marge_taux'" class="fas" :class="caretClass"></i>
                            </button>
                            <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" :title="rateLabel + ' : ' + rateFormula"></i>
                        </th>
                    </tr>
                </thead>
                <tbody v-if="loading">
                    <tr>
                        <td colspan="7" class="text-center"><i class="far fa-circle-notch fa-spin"></i></td>
                    </tr>
                </tbody>
                <tbody v-else-if="rows.length === 0">
                    <tr>
                        <td colspan="7" class="text-center text-muted">Aucun article sur ce périmètre</td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr v-for="row in rows" :key="row.id">
                        <td>{{ row.reference || '-' }}</td>
                        <td><a :href="row.url" class="ix-link">{{ row.name }}</a></td>
                        <td>
                            <span v-if="row.family" class="ix-text-small">{{ row.family }}</span>
                            <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-right">{{ formatCurrency(row.ha_total) }}</td>
                        <td class="text-right">{{ formatCurrency(row.ca_total) }}</td>
                        <td class="text-right">{{ row.marge_total === null ? '' : formatCurrency(row.marge_total) }}</td>
                        <td class="text-right">{{ row.marge_total === null ? '' : formatPercent(row.marge_taux) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="ix-table-bottom" v-if="!articleId && !loading">
            <div class="ix-table-bottom-left">
                <span class="ix-text-light" v-if="pagination.total > 0">
                    {{ pagination.from }} - {{ pagination.to }} sur {{ pagination.total }} {{ pagination.total > 1 ? 'résultats' : 'résultat' }}
                </span>
                <span class="ix-text-light" v-else>Pas de résultats</span>
            </div>
            <div class="ix-table-bottom-center"></div>
            <div class="ix-table-bottom-right">
                <nav class="ix-pagination-nav" v-if="pagination.last_page > 1">
                    <ul class="pagination ix-pagination">
                        <li v-if="pagination.current_page > 1">
                            <a href="#" rel="prev" aria-label="Précédent" class="ix-pagination-link" @click.prevent="goToPage(pagination.current_page - 1)">
                                <i class="far fa-angle-left"></i>
                            </a>
                        </li>

                        <template v-for="(page, index) in visiblePages">
                            <li v-if="page === 'dots'" :key="'dots-' + index" class="ix-pagination-link dots">...</li>
                            <li v-else-if="page === pagination.current_page" :key="'active-' + page" class="ix-pagination-link active">{{ page }}</li>
                            <li v-else :key="'link-' + page">
                                <a href="#" class="ix-pagination-link" @click.prevent="goToPage(page)">{{ page }}</a>
                            </li>
                        </template>

                        <li v-if="pagination.current_page < pagination.last_page">
                            <a href="#" rel="next" aria-label="Suivant" class="ix-pagination-link" @click.prevent="goToPage(pagination.current_page + 1)">
                                <i class="far fa-angle-right"></i>
                            </a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>

        <p class="text-muted mt-2 mb-0" style="font-size: 12px;">
            Ventes : factures et avoirs validés, commandes et devis exclus &middot; {{ period.label }}
        </p>
    </div>
</template>

<script>
import $ from "jquery";
import { toInputDate, buildPeriodPresets } from "../../periods";
import Multiselect from 'vue-multiselect'

const emptySynthesis = () => ({
    achats: { total_ht: 0 },
    ventes: { total_ht: 0, quantity: 0, average_price: null, average_price_evolution_percent: null },
    marge: { total_ht: 0, taux: null, evolution_points: null, uncosted_ca_ht: 0 },
});

const emptyTop3 = () => ({ ca: [], marge: [] });

export default {
    components: { Multiselect },
    props: {
        routeArticleTradeStats: String,
        familyOptions: {
            type: Array,
            default: () => [],
        },
        // Quand renseigné, le composant est scopé à un seul article (fiche
        // article, onglet "Performances commerciales") : pas de recherche, pas
        // de filtre famille, pas de tops 3, pas de tableau, pas d'état dans
        // l'URL (cf. §4.3 de la fiche de spec).
        articleId: {
            type: [String, Number],
            default: null,
        },
        // Paramètre margin_type du tenant : 2 = taux de marque, sinon taux de marge.
        marginType: {
            type: [String, Number],
            default: 1,
        },
    },
    data() {
        const now = new Date();

        return {
            selectedFamilies: [],
            includeSubfamilies: true,
            search: '',
            searchTimeout: null,
            selectedPreset: 'year',
            startAt: toInputDate(new Date(now.getFullYear(), 0, 1)),
            endAt: toInputDate(new Date(now.getFullYear(), 11, 31)),
            orderBy: 'ca_total',
            direction: 'desc',
            loading: false,
            period: { label: '' },
            synthesis: emptySynthesis(),
            top3: emptyTop3(),
            rows: [],
            pagination: { current_page: 1, last_page: 1, per_page: 25, total: 0, from: 0, to: 0 },
        }
    },
    computed: {
        presets() {
            return buildPeriodPresets();
        },
        caretClass() {
            return this.direction === 'asc' ? 'fa-caret-up' : 'fa-caret-down';
        },
        isBrandRate() {
            return Number(this.marginType) === 2;
        },
        rateLabel() {
            return this.isBrandRate ? 'Taux de marque' : 'Taux de marge';
        },
        rateFormula() {
            return this.isBrandRate ? 'Marge HT / ventes HT.' : 'Marge HT / coût des ventes HT.';
        },
        rateEvolutionTooltip() {
            return 'Écart en points du ' + this.rateLabel.toLowerCase() + ' par rapport à la même période l\'année précédente. Non calculable sur un cumul depuis l\'origine.';
        },
        /**
         * Reprend la règle d'affichage de components/pagination/default.blade.php :
         * première et dernière page toujours visibles, deux pages de part et
         * d'autre de la page courante, et des points de suspension pour combler.
         */
        visiblePages() {
            const current = this.pagination.current_page;
            const last = this.pagination.last_page;
            const pages = [];

            for (let page = 1; page <= last; page++) {
                if (current > 4 && page === 2) {
                    pages.push('dots');
                }

                if (page === current
                    || page === current + 1 || page === current + 2
                    || page === current - 1 || page === current - 2
                    || page === 1 || page === last) {
                    pages.push(page);
                }

                if (current < last - 3 && page === last - 1) {
                    pages.push('dots');
                }
            }

            return pages;
        },
        evolutionStyle() {
            const value = this.synthesis.marge.evolution_points;

            if (this.loading || value === null) return {};

            return { color: value >= 0 ? 'var(--ix-green)' : 'var(--ix-red)' };
        },
        priceEvolutionStyle() {
            const value = this.synthesis.ventes.average_price_evolution_percent;

            if (this.loading || value === null) return {};

            return { color: value >= 0 ? 'var(--ix-green)' : 'var(--ix-red)' };
        },
    },
    methods: {
        formatCurrency(value) {
            if (value === null || value === undefined) return '-,-€';

            return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
        },
        formatPercent(value) {
            if (value === null || value === undefined) return '-,- %';

            return Number(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %';
        },
        formatEvolution(value) {
            if (value === null || value === undefined) return '-';

            const formatted = Number(Math.abs(value)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            return (value >= 0 ? '+' : '-') + formatted + ' pts';
        },
        formatEvolutionPercent(value) {
            if (value === null || value === undefined) return '-';

            const formatted = Number(Math.abs(value)).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

            return (value >= 0 ? '+' : '-') + formatted + ' %';
        },
        onPresetChange() {
            if (this.selectedPreset === 'custom') {
                return;
            }

            const preset = this.presets.find((p) => p.key === this.selectedPreset);

            if (!preset) {
                return;
            }

            this.startAt = preset.start;
            this.endAt = preset.end;
            this.fetchStats(1);
        },
        onDateChange() {
            // Une saisie manuelle de date sort forcément d'un preset.
            this.selectedPreset = 'custom';
            this.fetchStats(1);
        },
        sortBy(field) {
            if (this.orderBy === field) {
                this.direction = this.direction === 'asc' ? 'desc' : 'asc';
            } else {
                this.orderBy = field;
                this.direction = 'desc';
            }

            this.fetchStats(1);
        },
        onSearchInput() {
            // Une frappe ne doit pas déclencher une requête par caractère.
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.fetchStats(1), 400);
        },
        onFilterChange() {
            this.fetchStats(1);
        },
        goToPage(page) {
            this.fetchStats(page);
        },
        buildQuery(page) {
            const params = new URLSearchParams();

            if (this.articleId) {
                params.append('article_id', this.articleId);
                if (this.startAt) params.append('period_start', this.startAt);
                if (this.endAt) params.append('period_end', this.endAt);

                return '?' + params.toString();
            }

            if (this.selectedFamilies.length) {
                params.append('article_family_id', this.selectedFamilies.map((f) => f.id).join(';'));
            }

            params.append('include_subfamilies', this.includeSubfamilies ? '1' : '0');

            if (this.search) params.append('q', this.search);
            if (this.startAt) params.append('period_start', this.startAt);
            if (this.endAt) params.append('period_end', this.endAt);

            params.append('order_by', this.orderBy);
            params.append('direction', this.direction);
            params.append('page', page);

            return '?' + params.toString();
        },
        /**
         * Restaure l'état depuis la query-string, pour qu'un rechargement ou un
         * lien partagé retombe sur les mêmes filtres, tri et page.
         */
        restoreFromUrl() {
            // Scopé à un article : la page porte déjà son propre ?tab=, pas de
            // filtres à restaurer depuis l'URL.
            if (this.articleId) {
                return 1;
            }

            const params = new URLSearchParams(window.location.search);

            const families = params.get('familles');
            if (families) {
                const ids = families.split(';');
                this.selectedFamilies = this.familyOptions.filter((option) => ids.includes(String(option.id)));
            }

            if (params.get('sous_familles') === '0') {
                this.includeSubfamilies = false;
            }

            if (params.get('recherche')) {
                this.search = params.get('recherche');
            }

            const period = params.get('periode');
            if (period === 'custom') {
                this.selectedPreset = 'custom';
                this.startAt = params.get('du') || '';
                this.endAt = params.get('au') || '';
            } else if (period) {
                // Un preset est recalculé par rapport à aujourd'hui : un lien
                // "mois en cours" partagé reste sur le mois en cours.
                const preset = this.presets.find((p) => p.key === period);
                if (preset) {
                    this.selectedPreset = preset.key;
                    this.startAt = preset.start;
                    this.endAt = preset.end;
                }
            }

            if (params.get('tri')) {
                this.orderBy = params.get('tri');
            }

            if (params.get('sens') === 'asc') {
                this.direction = 'asc';
            }

            const page = parseInt(params.get('page'), 10);

            return page > 0 ? page : 1;
        },
        /**
         * Écrit l'état courant dans la query-string sans empiler d'entrée dans
         * l'historique : le bouton retour sort de la page plutôt que de rejouer
         * chaque changement de filtre.
         */
        syncUrl(page) {
            if (this.articleId) {
                return;
            }

            const params = new URLSearchParams(window.location.search);

            const set = (key, value, keep) => keep ? params.set(key, value) : params.delete(key);

            set('familles', this.selectedFamilies.map((f) => f.id).join(';'), this.selectedFamilies.length > 0);
            set('sous_familles', '0', !this.includeSubfamilies);
            set('recherche', this.search, !!this.search);
            set('periode', this.selectedPreset, this.selectedPreset !== 'year');
            set('du', this.startAt, this.selectedPreset === 'custom' && !!this.startAt);
            set('au', this.endAt, this.selectedPreset === 'custom' && !!this.endAt);
            set('tri', this.orderBy, this.orderBy !== 'ca_total');
            set('sens', this.direction, this.direction !== 'desc');
            set('page', page, page > 1);

            const query = params.toString();

            window.history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
        },
        async fetchStats(page = 1) {
            if (this.startAt && this.endAt && this.startAt > this.endAt) {
                return;
            }

            this.loading = true;
            this.syncUrl(page);

            await fetch(this.routeArticleTradeStats + this.buildQuery(page), {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                return response.json();
            }).then((data) => {
                this.rows = data.rows;
                this.synthesis = data.synthesis;
                this.top3 = data.top3;
                this.period = data.period;
                this.pagination = data.pagination;
            }).catch(() => {
                this.rows = [];
                this.synthesis = emptySynthesis();
                this.top3 = emptyTop3();
                this.pagination = { current_page: 1, last_page: 1, per_page: 25, total: 0, from: 0, to: 0 };
            });

            this.loading = false;
            this.$nextTick(() => {
                $('[data-toggle="tooltip"]:not(.copy-button)').tooltip();
            });
        },
    },
    mounted() {
        const page = this.restoreFromUrl();
        const pane = this.$el.closest('.tab-pane');

        // La requête est coûteuse : on ne la lance pas tant que l'onglet n'a
        // pas été ouvert au moins une fois.
        if (!pane || pane.classList.contains('active')) {
            this.fetchStats(page);

            return;
        }

        $('a[href="#' + pane.id + '"]').one('shown.bs.tab', () => this.fetchStats(page));
    }
}
</script>
